import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SiteLayout } from './components/SiteLayout';
import PrivacyPage from './pages/PrivacyPage';
import ConsentPage from './pages/ConsentPage';
import MediaConsentPage from './pages/MediaConsentPage';
import TermsPage from './pages/TermsPage';
import NotFoundPage from './pages/NotFoundPage';
import { adminBasePath } from './adminPath';
import './styles/global.css';

/**
 * Lazy route modules. Keys are the source paths Vite writes into the build manifest, so the server
 * can link each page's CSS/JS and the client can preload the chunk before hydrating.
 */
export const routeModules = {
  'src/app/pages/HomePage.tsx': () => import('./pages/HomePage'),
  'src/app/pages/VtdPage.tsx': () => import('./pages/VtdPage'),
  'src/app/pages/KttuPage.tsx': () => import('./pages/KttuPage'),
  'src/app/pages/ExcursionPage.tsx': () => import('./pages/ExcursionPage'),
  'src/app/pages/BotanicalPage.tsx': () => import('./pages/BotanicalPage'),
  'src/app/pages/SchoolsPage.tsx': () => import('./pages/SchoolsPage'),
  'src/app/pages/CoursesPage.tsx': () => import('./pages/CoursesPage'),
  'src/app/pages/PartnersPage.tsx': () => import('./pages/PartnersPage'),
  'src/app/pages/FounderPage.tsx': () => import('./pages/FounderPage'),
  'src/app/pages/GiveawayPage.tsx': () => import('./pages/GiveawayPage'),
  'src/app/pages/GiveawayRulesPage.tsx': () => import('./pages/GiveawayRulesPage'),
  'src/app/pages/LoginPage.tsx': () => import('./pages/LoginPage'),
  'src/app/pages/AdminPage.tsx': () => import('./pages/AdminPage'),
  'src/app/telegram/TelegramAppPage.tsx': () => import('./telegram/TelegramAppPage'),
} as const;
export type RouteModule = keyof typeof routeModules;

const HomePage = lazy(routeModules['src/app/pages/HomePage.tsx']);
const VtdPage = lazy(routeModules['src/app/pages/VtdPage.tsx']);
const KttuPage = lazy(routeModules['src/app/pages/KttuPage.tsx']);
const ExcursionPage = lazy(routeModules['src/app/pages/ExcursionPage.tsx']);
const BotanicalPage = lazy(routeModules['src/app/pages/BotanicalPage.tsx']);
const SchoolsPage = lazy(routeModules['src/app/pages/SchoolsPage.tsx']);
const CoursesPage = lazy(routeModules['src/app/pages/CoursesPage.tsx']);
const PartnersPage = lazy(routeModules['src/app/pages/PartnersPage.tsx']);
const FounderPage = lazy(routeModules['src/app/pages/FounderPage.tsx']);
const GiveawayPage = lazy(routeModules['src/app/pages/GiveawayPage.tsx']);
const GiveawayRulesPage = lazy(routeModules['src/app/pages/GiveawayRulesPage.tsx']);
const LoginPage = lazy(routeModules['src/app/pages/LoginPage.tsx']);
const AdminPage = lazy(routeModules['src/app/pages/AdminPage.tsx']);
const TelegramAppPage = lazy(routeModules['src/app/telegram/TelegramAppPage.tsx']);

/** The home page used to live at /excursions; the server 301s it, this covers in-app links. */
function LegacyHomeRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`/${search}${hash}`} replace />;
}

/** Router-agnostic route tree: BrowserRouter in the client, StaticRouter during server rendering. */
export function AppRoutes() {
  // Present only in the admin shell; elsewhere the staff routes do not exist and fall to the 404 page.
  const adminBase = adminBasePath()?.slice(1);
  return (
    <Suspense fallback={<div className="route-loading" role="status">Загружаем маршрут…</div>}>
      <Routes>
        <Route element={<SiteLayout />}>
          <Route index element={<HomePage />} />
          <Route path="excursions" element={<LegacyHomeRedirect />} />
          <Route path="excursions/vostochnoe-tramvaynoe-depo" element={<VtdPage />} />
          <Route path="excursions/zapadnoe-tramvaynoe-depo" element={<ExcursionPage key="western_tram_depot" excursion="western_tram_depot" />} />
          <Route path="excursions/pervoe-trolleybusnoe-depo" element={<ExcursionPage key="trolleybus_depot_1" excursion="trolleybus_depot_1" />} />
          <Route path="excursions/tramvay-po-gorodu" element={<ExcursionPage key="city_tram" excursion="city_tram" />} />
          <Route path="excursions/tramvay-nestandartnyy-marshrut" element={<ExcursionPage key="city_tram_custom" excursion="city_tram_custom" />} />
          <Route path="excursions/karaoke-na-kolesah" element={<ExcursionPage key="tram_karaoke" excursion="tram_karaoke" />} />
          <Route path="excursions/kttu" element={<KttuPage />} />
          <Route path="excursions/botanicheskaya-progulka" element={<BotanicalPage />} />
          <Route path="shkolam" element={<SchoolsPage />} />
          <Route path="kursy" element={<CoursesPage />} />
          <Route path="partners" element={<PartnersPage />} />
          <Route path="osnovatel" element={<FounderPage />} />
          <Route path="rozygrysh" element={<GiveawayPage />} />
          <Route path="rozygrysh/pravila" element={<GiveawayRulesPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="consent" element={<ConsentPage />} />
          <Route path="media-consent" element={<MediaConsentPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        {adminBase && <Route path={`${adminBase}/login`} element={<LoginPage />} />}
        {adminBase && <Route path={adminBase} element={<AdminPage />} />}
        <Route path="tg" element={<TelegramAppPage />} />
        <Route path="tg/memo" element={<TelegramAppPage />} />
        <Route path="tg/rental" element={<TelegramAppPage />} />
        <Route path="tg/people" element={<TelegramAppPage />} />
        <Route path="tg/call" element={<TelegramAppPage />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  return <BrowserRouter><AppRoutes /></BrowserRouter>;
}
